/**
 * Placeholders (RFP C3) in two states: the assigned, dated SOC hold and the
 * unassigned tail reserved per week against a discipline + trait group, in
 * points (hours kept alongside for the hours engine). Holds carry the 48h SOC
 * clock from the expected discharge; a hold that passed its date without
 * converting shows as expired on the pending-admissions list, never silently
 * released.
 */
import type { ConfigState, DateKey, DemoState, Discipline, FrequencyOrder, Hold, IsoString, Referral, TailReservation } from "@caredna-marlin/utils/homeHealthDemo/state";
import { addDaysToKey, dateKeyStart } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { socDueWindow } from "./socWindow";

export { socDueWindow };
import { expectedPlanFor } from "./expectedPlan";
import { parseFrequency } from "@caredna-marlin/utils/homeHealthDemo/engine/frequency";
import { routineVisitTypeFor } from "@caredna-marlin/utils/homeHealthDemo/engine/visitTypes";
import { ROUTINE_DRIVE_MINUTES } from "./virtualCensus";

export const SOC_WINDOW_HOURS = 48;

/** The instant the 48h clock runs out: midnight after the second day. */
export function socDueByIso(referral: Pick<Referral, "expectedDischarge">, timeZone: string): IsoString {
  return dateKeyStart(addDaysToKey(referral.expectedDischarge, 2), timeZone).toISOString();
}

/**
 * Orders that carry frequencies win; eval-and-treat orders fall back to the case
 * type's starter defaults.
 *
 * @deprecated for anything the screen reads — `expectedPlanFor` is the one
 * selector, and it also honours the template or Custom plan the branch chose.
 * Kept because the starter-default fallback is still the shape the store seed
 * and the plan engine build episodes from.
 */
export function effectiveOrders(referral: Pick<Referral, "orders" | "caseType">, config: Pick<ConfigState, "starterFrequencies">): { orders: FrequencyOrder[]; source: "orders" | "starterDefaults" } {
  const withFrequencies = referral.orders.filter((o) => !parseFrequency(o.pattern).evalOnly);
  if (withFrequencies.length === referral.orders.length && withFrequencies.length > 0) return { orders: referral.orders, source: "orders" };
  const defaults = config.starterFrequencies[referral.caseType] ?? [];
  // Keep the traits the referral asked for on the disciplines the defaults cover.
  const orders = defaults.map((d) => {
    const asked = referral.orders.find((o) => o.discipline === d.discipline);
    const traits = Array.from(new Set([...(d.requiredTraits ?? []), ...(asked?.requiredTraits ?? [])]));
    return traits.length ? { ...d, requiredTraits: traits } : d;
  });
  return { orders, source: "starterDefaults" };
}

/** All-in hours per visit for a discipline's routine visit. */
export function allInRoutineHours(config: ConfigState, discipline: Discipline): number {
  return (routineVisitTypeFor(config, discipline).plannedMinutes + ROUTINE_DRIVE_MINUTES) / 60;
}

/**
 * The tail: reserved points (and hours) per week over the first `weeks` weeks,
 * using the average weekly frequency of the order over that span.
 */
export function buildTail(orders: FrequencyOrder[], config: ConfigState, weeks = 4): TailReservation[] {
  const out: TailReservation[] = [];
  for (const order of orders) {
    const parsed = parseFrequency(order.pattern);
    if (parsed.evalOnly) continue;
    let visits = 0;
    let remaining = weeks;
    for (const seg of parsed.segments) {
      const take = Math.min(seg.weeks, remaining);
      visits += (seg.perWeek * take) / seg.everyWeeks;
      remaining -= take;
      if (remaining <= 0) break;
    }
    const covered = weeks - Math.max(remaining, 0);
    if (covered <= 0) continue;
    const perWeek = visits / covered;
    const hoursPerWeek = Math.round(perWeek * allInRoutineHours(config, order.discipline) * 10) / 10;
    const pointsPerWeek = Math.round(perWeek * routineVisitTypeFor(config, order.discipline).points * 4) / 4;
    out.push({ discipline: order.discipline, requiredTraits: order.requiredTraits ?? [], hoursPerWeek, pointsPerWeek, weeks: covered });
  }
  return out;
}

export interface HoldSlot {
  providerHandle?: string;
  date: DateKey;
  windowStart: string;
  windowEnd: string;
}

/**
 * The hold reserves what the expected plan says, over the plan's own horizon:
 * one selector for the tail, the panel's capacity row and the hold alike.
 */
export function createHold(referral: Referral, slot: HoldSlot, config: ConfigState): Hold {
  const plan = expectedPlanFor(config, referral);
  return {
    id: `hold-${referral.id}`,
    referralId: referral.id,
    patientRef: referral.patientRef,
    providerHandle: slot.providerHandle,
    // Who it was put on, kept for the day it loses its hour: see `authoredHandle`.
    authoredHandle: slot.providerHandle,
    date: slot.date,
    windowStart: slot.windowStart,
    windowEnd: slot.windowEnd,
    status: "held",
    tail: buildTail(plan.orders, config, plan.horizonWeeks),
  };
}

export function moveHold(hold: Hold, slot: HoldSlot): Hold {
  // A re-hold is intake putting it somewhere again, so it re-authors it.
  return { ...hold, providerHandle: slot.providerHandle, authoredHandle: slot.providerHandle ?? hold.authoredHandle, date: slot.date, windowStart: slot.windowStart, windowEnd: slot.windowEnd };
}

export function releaseHold(hold: Hold): Hold {
  return { ...hold, status: "released" };
}

/** Holds still holding a slot (not converted, not released). */
export function activeHolds(state: Pick<DemoState, "holds">): Hold[] {
  return Object.values(state.holds).filter((h) => h.status === "held");
}

/** A hold whose day passed without conversion. */
export function isHoldExpired(hold: Hold, today: DateKey): boolean {
  return hold.status === "held" && hold.date < today;
}

/** Window end for a hold slot: start + SOC duration + drive, as "HH:mm". */
export function holdWindowEnd(windowStart: string, config: Pick<ConfigState, "socSlot">): string {
  const [h, m] = windowStart.split(":").map(Number);
  const endMinutes = h * 60 + m + config.socSlot.durationMinutes + config.socSlot.driveMinutes;
  return `${String(Math.floor(endMinutes / 60)).padStart(2, "0")}:${String(endMinutes % 60).padStart(2, "0")}`;
}
